import { defineStore } from 'pinia'
import { ref, reactive, computed } from 'vue'
import { useApi } from '@/utils/axios'
import { REVENDA_METRICAS_ROTAS } from '@/utils/apiRoutes'

export const useRevendasVitrineStore = defineStore('revendasVitrine', () => {
  // state
  const items = ref([])
  const meta = reactive({
    current_page: 1,
    total: 0,
    per_page: 10,
    last_page: 1,
  })
  const loading = ref(false)
  const error = ref(null)

  // filtros padrão
  const filters = reactive({
    data_inicio: null,
    data_fim: null,
    idrevenda: null,
    idrevendedor: null,
    gas_do_povo: null,
    revenda_rapida: null,
    per_page: 10,
    page: 1,
  })

  // computed helpers
  const total = computed(() => meta.total)
  const currentPage = computed(() => meta.current_page)
  const lastPage = computed(() => meta.last_page)
  const perPage = computed(() => meta.per_page)

  // fetch com parâmetros
  async function fetchMetricas(options = {}) {
    loading.value = true
    error.value = null

    const params = {
      data_inicio: options.data_inicio !== undefined ? options.data_inicio : filters.data_inicio,
      data_fim: options.data_fim !== undefined ? options.data_fim : filters.data_fim,
      idrevenda: options.idrevenda !== undefined ? options.idrevenda : filters.idrevenda,
      idrevendedor:
        options.idrevendedor !== undefined ? options.idrevendedor : filters.idrevendedor,
      gas_do_povo: options.gas_do_povo !== undefined ? options.gas_do_povo : filters.gas_do_povo,
      revenda_rapida:
        options.revenda_rapida !== undefined ? options.revenda_rapida : filters.revenda_rapida,
      per_page: options.per_page !== undefined ? options.per_page : filters.per_page,
      page: options.page !== undefined ? options.page : filters.page,
    }

    try {
      const response = await useApi(REVENDA_METRICAS_ROTAS.METRICAS).fetch({ params })
      // backend: { status, data: { data: [...], current_page, last_page, total, per_page } }
      const pagination = response.data ?? {}
      items.value = Array.isArray(pagination.data) ? pagination.data : []
      meta.current_page = pagination.current_page ?? params.page ?? 1
      meta.total = pagination.total ?? 0
      meta.per_page = pagination.per_page ?? params.per_page ?? 10
      meta.last_page = pagination.last_page ?? 1

      // persistir últimos filtros usados
      filters.data_inicio = params.data_inicio ?? null
      filters.data_fim = params.data_fim ?? null
      filters.idrevenda = params.idrevenda ?? null
      filters.idrevendedor = params.idrevendedor ?? null
      filters.gas_do_povo = params.gas_do_povo ?? null
      filters.revenda_rapida = params.revenda_rapida ?? null
      filters.per_page = params.per_page ?? filters.per_page
      filters.page = params.page ?? filters.page

      return { success: true, data: items.value, meta }
    } catch (err) {
      error.value = err.response?.data?.message || err.message || 'Erro ao carregar métricas'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  function setPage(p) {
    filters.page = p
    return fetchMetricas({ page: p })
  }

  function setPerPage(pp) {
    filters.per_page = pp
    filters.page = 1
    return fetchMetricas({ per_page: pp, page: 1 })
  }

  function resetFilters() {
    filters.data_inicio = null
    filters.data_fim = null
    filters.idrevenda = null
    filters.idrevendedor = null
    filters.gas_do_povo = null
    filters.revenda_rapida = null
    filters.per_page = 10
    filters.page = 1

    fetchMetricas({})
  }

  function clearData() {
    items.value = []
    meta.current_page = 1
    meta.total = 0
    meta.per_page = 10
    meta.last_page = 1
    error.value = null
  }

  return {
    // state
    items,
    meta,
    loading,
    error,
    filters,

    // computed
    total,
    currentPage,
    lastPage,
    perPage,

    // actions
    fetchMetricas,
    setPage,
    setPerPage,
    resetFilters,
    clearData,
  }
})
